import { createServerFn } from "@tanstack/react-start";
import { eq } from "drizzle-orm";
import { getDb } from "@/db";
import { liveState } from "@/db/schema";
import { getSession } from "@/lib/auth-session";

async function requireAdmin() {
	const session = await getSession();
	if (!session?.user) throw new Error("Unauthorized");
	const role = (session.user as { role?: string }).role;
	if (role !== "admin" && role !== "operator") throw new Error("Forbidden");
}

// ── Scene types ──────────────────────────────────────────────────────

export type LiveScene =
	| { scene: "leaderboard" }
	| { scene: "upcoming" }
	| {
			scene: "lineup";
			heat: number;
			lanes: Array<{ lane: number; carId: number }>;
	  }
	| {
			scene: "racing";
			heat: number;
			lanes: Array<{
				lane: number;
				carId: number;
				elapsedTimeMs?: number | null;
				status?: string;
			}>;
			startedAt: number;
	  }
	| {
			scene: "results";
			heat: number;
			lanes: Array<{
				lane: number;
				carId: number;
				elapsedTimeMs: number | null;
				status: string;
			}>;
			newRecords?: Array<{ lane: number; division?: string | null }>;
	  }
	| {
			scene: "awards";
	  }
	| {
			scene: "announcement";
			message: string;
			subtitle?: string;
	  };

// ── Server functions ─────────────────────────────────────────────────

export const updateLiveState = createServerFn({ method: "POST" })
	.inputValidator((data: { eventId: number; scene: LiveScene }) => data)
	.handler(async ({ data }) => {
		await requireAdmin();
		const db = getDb();
		const existing = await db
			.select()
			.from(liveState)
			.where(eq(liveState.eventId, data.eventId))
			.get();

		if (existing) {
			await db
				.update(liveState)
				.set({
					scene: data.scene.scene,
					data: JSON.stringify(data.scene),
					updatedAt: new Date(),
				})
				.where(eq(liveState.eventId, data.eventId));
		} else {
			await db.insert(liveState).values({
				eventId: data.eventId,
				scene: data.scene.scene,
				data: JSON.stringify(data.scene),
			});
		}
	});

export const getLiveState = createServerFn({ method: "GET" })
	.inputValidator((eventId: string) => eventId)
	.handler(async ({ data: eventId }) => {
		const db = getDb();
		const row = await db
			.select()
			.from(liveState)
			.where(eq(liveState.eventId, Number(eventId)))
			.get();

		if (!row) return { scene: "upcoming" } as LiveScene;

		try {
			return JSON.parse(row.data) as LiveScene;
		} catch {
			return { scene: "upcoming" } as LiveScene;
		}
	});
